import { useCallback, useMemo, useRef } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import TermsPage from "./pages/terms/TermsPage";
import Dashboard from "./pages/Dashboard";
import { CotiPluginProvider } from "@/context/PrivacyBridgeContext";
import { ThemeProvider } from "@/providers/ThemeProvider";
import { WagmiRainbowKitProvider } from "@/providers/WagmiRainbowKitProvider";
import NetworkGuard from "@/components/modals/NetworkGuard";
import InstallMetaMaskModal from "@/components/modals/InstallMetaMaskModal";
import ZerionConnectHelpModal from "@/components/modals/ZerionConnectHelpModal";
import BridgeStatisticsPage from "./pages/BridgeStatisticsPage";
import ProtectedDashboardRoute from "@/components/ProtectedDashboardRoute";
import { useOnboardModalTheme } from "@/hooks/useOnboardModalTheme";
import GlobalErrorHandler from "@/components/errors/GlobalErrorHandler";
import PrivateUnlockRefreshBridge, {
  type RefreshPrivateBalancesFn,
} from "@/components/PrivateUnlockRefreshBridge";
import AesSessionMemoryDebug from "@/components/AesSessionMemoryDebug";

const AppProviders = ({ children }: { children: React.ReactNode }) => {
  const onboardTheme = useOnboardModalTheme();
  const refreshRef = useRef<RefreshPrivateBalancesFn | null>(null);

  const registerRefresh = useCallback((refresh: RefreshPrivateBalancesFn | null) => {
    refreshRef.current = refresh;
  }, []);

  // Fires after restore, manual AES key, or contract onboarding — the latter
  // when the user dismisses the onboard modal, so the read happens after the
  // onboarding tx has settled.
  const handleUnlocked = useCallback(async () => {
    try {
      await refreshRef.current?.();
    } catch (err) {
      console.error("[AppProviders] Post-unlock balance refresh failed", err);
    }
  }, []);

  const privateUnlock = useMemo(
    () => ({
      theme: onboardTheme,
      warnings: {
        intro: "Unlock private tokens in Privacy Portal using Onboard.",
      },
      onUnlocked: handleUnlocked,
    }),
    [onboardTheme, handleUnlocked],
  );

  return (
    <CotiPluginProvider features={['tokens', 'portal', 'pod']} privateUnlock={privateUnlock}>
      <PrivateUnlockRefreshBridge register={registerRefresh} />
      <AesSessionMemoryDebug />
      {children}
    </CotiPluginProvider>
  );
};

const App = () => {
  return (
    <ThemeProvider>
      <GlobalErrorHandler>
        <WagmiRainbowKitProvider>
          <TooltipProvider>
            <AppProviders>
              <Toaster />
              <Sonner />
              <InstallMetaMaskModal />
              <ZerionConnectHelpModal />
              <BrowserRouter>
                <NetworkGuard>
                  <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/backoffice" element={<ProtectedDashboardRoute><Dashboard /></ProtectedDashboardRoute>} />
                    <Route path="/terms" element={<TermsPage />} />
                    <Route
                      path="/backoffice/bridge/:bridgeAddress/statistics"
                      element={
                        <ProtectedDashboardRoute>
                          <BridgeStatisticsPage />
                        </ProtectedDashboardRoute>
                      }
                    />
                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </NetworkGuard>
              </BrowserRouter>
            </AppProviders>
          </TooltipProvider>
        </WagmiRainbowKitProvider>
      </GlobalErrorHandler>
    </ThemeProvider>
  );
};

export default App;
